import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';

import { LanguageSlugEnum } from '@/types/regionTypes';

import type {
  AccountError,
  AddressFragment,
  AddressTypeEnum,
  MetadataItem,
} from '@/graphql/saleor/api';
import type { UseCheckout } from '@/hooks/useCheckout';
import { addressSetDefault, addressToInput, getMetaValue } from '@/lib/address';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import JP_PREFECTURE from '@/lib/prefecture.json';

export const commonSetAsPrimaryAddressLogic = async (
  address: AddressFragment,
  id: string,
  type: AddressTypeEnum,
  addressTitleUpdate?: UseCheckout['addressTitleUpdate'],
  addressUpdate?: UseCheckout['addressUpdate'],
  billingTitle?: string,
  shippingTitle?: string,
) => {
  let errors: AccountError[] = [];
  const res = await addressSetDefault({ id, type });
  errors = res.errors;

  // Update the checkout address on demand
  if (addressTitleUpdate && addressUpdate && isEmpty(errors)) {
    shippingTitle =
      type === 'SHIPPING'
        ? getMetaValue(
            ACCOUNT_METADATA.personalTitle,
            address.metadata as MetadataItem[],
          )
        : shippingTitle;
    billingTitle =
      type === 'BILLING'
        ? getMetaValue(
            ACCOUNT_METADATA.personalTitle,
            address.metadata as MetadataItem[],
          )
        : billingTitle;
    addressTitleUpdate?.(shippingTitle ?? 'Mr', billingTitle ?? 'Mr');
    addressUpdate?.(
      type.toLowerCase() as 'shipping' | 'billing',
      addressToInput({
        ...address,
        metadata: address.metadata.map((item: MetadataItem) =>
          pick(item, ['key', 'value']),
        ),
      }),
    );
  }
  return errors;
};

export const isLatin = (str: string) => /^[A-Za-z\s]+$/.test(str);
export const isKanji = (str: string) => /^[\u4e00-\u9fa5]+$/.test(str);

export const showKanjiLabel = (languageCode?: string) => {
  return languageCode
    ? languageCode === LanguageSlugEnum.ja ||
        languageCode === LanguageSlugEnum.zh
    : false;
};

export const getPrefectureLabelByValue = (
  kanji: boolean,
  selectedValue?: string,
) => {
  if (!selectedValue) return '';
  const prefectureOptions = kanji
    ? JP_PREFECTURE.filter((item) => isKanji(item.label))
    : JP_PREFECTURE.filter((item) => isLatin(item.label));
  return (
    prefectureOptions.filter((item) => item.value === selectedValue)?.[0]
      ?.label ?? ''
  );
};
